// Imported from the `/urql` subpath, not the package root: it is the client the
// Agent Manager file queries live on (see `src/gql/agent-manager.gql`).
import { proStudioUrqlGql as agentManagerGql } from '@pypestream/api-services/urql';

import { buildUploadChanges, syncAgentManagerFiles } from './agent-manager-api';

/**
 * One Files → Media entry of an Agent Manager v2 solution, in the shape
 * `ctx.selectedBot.assets` carries (`{ id, url }`) plus what a write needs:
 * the repository `path` (delete) and the `sha` (an update is sha-checked).
 */
export type AgentManagerMediaAsset = {
  id: string;
  url: string;
  path: string;
  sha?: string;
  /** For the card's preview Blob — the route's body may carry no usable type. */
  mimeType?: string;
};

/**
 * Lists the media (`assets/`) of a solution version from the Agent Manager.
 * Throws on a transport or GraphQL error — a caller that must not fail on it
 * (the solution load) catches and degrades.
 */
export const loadAgentManagerAssets = async (
  solutionId: string,
  versionName: string
): Promise<AgentManagerMediaAsset[]> => {
  const { data, error } = await agentManagerGql.getAssetsFiles({
    solutionId,
    versionName,
  });
  if (error) throw error;

  return (data?.admin_?.assetsFiles ?? [])
    .filter((file) => file?.url && file?.path)
    .map((file) => ({
      // Keyed by path, not sha: two files with the same bytes share a git blob
      // sha, and a sha key then deleted the wrong file and dropped both cards.
      id: file?.path as string,
      url: file?.url as string,
      path: file?.path as string,
      sha: file?.sha ?? undefined,
      mimeType: file?.mimeType ?? undefined,
    }));
};

/**
 * A queue that runs async tasks strictly one at a time, in call order. A failed
 * task rejects its own caller and does not stop the ones queued after it.
 * `pending` counts the tasks not yet settled, so a caller can act once the queue
 * drains.
 *
 * Media needs it because filepond calls `onUpload` once PER FILE: picking two
 * files fired two concurrent `agent-manager-sync` commits onto the same branch,
 * and the second lost the race (PSTUDIO-4354; the same class as PSTUDIO-3173).
 */
export const createSerialQueue = () => {
  let tail: Promise<unknown> = Promise.resolve();
  let pending = 0;

  const run = <T>(task: () => Promise<T>): Promise<T> => {
    pending += 1;
    const result = tail.then(task);
    const settle = () => {
      pending -= 1;
    };
    tail = result.then(settle, settle);
    return result;
  };

  return Object.assign(run, {
    get pending() {
      return pending;
    },
  });
};

/**
 * Uploads picked media into the solution's `assets/` folder as one commit. A
 * file whose path already exists is sent as a sha-checked `update`, so a
 * concurrent change surfaces as a conflict instead of being overwritten.
 *
 * The existing list is read HERE, when the upload runs — not passed in. Media
 * queues one upload per picked file, and a list captured when the batch started
 * made a second upload of the same name an `add` for a path the first had just
 * committed (or an `update` with its pre-commit sha): a 409 either way.
 */
export const uploadMediaAssets = async ({
  solutionId,
  versionName,
  files,
}: {
  solutionId: string;
  versionName: string;
  files: File[];
}) => {
  const existing = await loadAgentManagerAssets(solutionId, versionName);
  return syncAgentManagerFiles({
    solutionId,
    versionName,
    commitMessage: `Uploaded ${files.length} media file(s)`,
    changes: buildUploadChanges({
      type: 'assets',
      files: files.map((file) => ({ relativePath: file.name, file })),
      existing,
    }),
  });
};
